/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form as UnformForm } from '@unform/web';
import { FormHandles } from '@unform/core';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import { FiDollarSign } from 'react-icons/fi';

import Toolbar from '../../components/Toolbar';
import LoadingAnimation from '../../components/Loading';
import Input from '../../components/Input';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';

import { Allocation, BuyAsset, BuyTD } from '../PortfolioAllocation/interfaces';

import {
  Container,
  Form,
  Content,
  HeaderRow,
  Title,
  ControlsRow,
  ChartsGrid,
  ChartCard,
  ChartTitle,
  Table,
  PrimaryButton,
} from './styles';

interface Stock { code: string; name: string; quantity: number; unit_price: number; }
interface Fii { code: string; name: string; quantity: number; unit_price: number; }
interface Tesouro { code: string; name: string; quantity: number; amount_invested: number; current_value: number; expiration_date: string; }
interface Portfolio { stocks: Stock[]; fiis: Fii[]; tesouro: Tesouro[]; }

type RedistributionTrade = {
  type: 'STOCK' | 'FII' | 'TD';
  code: string;
  name: string;
  current_value: number;
  target_value: number;
  current_percentage: number;
  target_percentage: number;
  action: 'buy' | 'sell';
  amount: number;
};

interface RedistributionResponse {
  current_total_value: number;
  target_total_value: number;
  total_weight: number;
  trades: {
    stocks: RedistributionTrade[];
    fiis: RedistributionTrade[];
    tesouro: RedistributionTrade[];
  };
}

interface AllocationFormData { amountToInvest: number; }

const palette = ['#1DB954', '#7086f5', '#C9A227'];

const PortfolioAllocationV2: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [fiis, setFiis] = useState<Fii[]>([]);
  const [tesouro, setTesouro] = useState<Tesouro[]>([]);

  const [stocksToBuy, setStocksToBuy] = useState<BuyAsset[]>([]);
  const [fiisToBuy, setFiisToBuy] = useState<BuyAsset[]>([]);
  const [tdToBuy, setTDToBuy] = useState<BuyTD[]>([]);
  const [stocksToSell, setStocksToSell] = useState<string[]>([]);
  const [fiisToSell, setFiisToSell] = useState<string[]>([]);

  const [reallocTotals, setReallocTotals] = useState<{ stocks: number; fiis: number; td: number } | null>(null);

  const [redistributionResult, setRedistributionResult] = useState<RedistributionResponse | null>(null);
  const [redistributing, setRedistributing] = useState(false);
  const [activeMode, setActiveMode] = useState<'allocation' | 'redistribution' | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const allocateAssetsFormRef = useRef<FormHandles>(null);

  const reloadPositions = useCallback(() => {
    return api.get<Portfolio>('wallet/positions').then((response) => {
      const p = response.data;
      setStocks(p.stocks || []);
      setFiis(p.fiis || []);
      setTesouro(p.tesouro || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    reloadPositions().catch(() => {
      addToast({ type: 'error', title: 'Erro', description: 'Falha ao carregar dados da carteira' });
    }).finally(() => setLoading(false));
  }, [reloadPositions, addToast]);

  const totals = useMemo(() => {
    const stocksCurrent = stocks.reduce((s, it) => s + (it.unit_price / 100) * it.quantity, 0);
    const fiisCurrent = fiis.reduce((s, it) => s + (it.unit_price / 100) * it.quantity, 0);
    const tdCurrent = tesouro.reduce((s, it) => s + it.current_value / 100, 0);
    return { stocksCurrent, fiisCurrent, tdCurrent, total: stocksCurrent + fiisCurrent + tdCurrent };
  }, [stocks, fiis, tesouro]);

  const redistributionTotals = useMemo(() => {
    if (!redistributionResult) return null;
    const sumCurrent = (list: RedistributionTrade[]) => list.reduce((s, it) => s + (it.current_value || 0) / 100, 0);
    const sumTarget = (list: RedistributionTrade[]) => list.reduce((s, it) => s + (it.target_value || 0) / 100, 0);

    const stocksCurrent = sumCurrent(redistributionResult.trades?.stocks || []);
    const fiisCurrent = sumCurrent(redistributionResult.trades?.fiis || []);
    const tdCurrent = sumCurrent(redistributionResult.trades?.tesouro || []);

    const stocksTarget = sumTarget(redistributionResult.trades?.stocks || []);
    const fiisTarget = sumTarget(redistributionResult.trades?.fiis || []);
    const tdTarget = sumTarget(redistributionResult.trades?.tesouro || []);

    const currentTotal = stocksCurrent + fiisCurrent + tdCurrent;
    const targetTotal = stocksTarget + fiisTarget + tdTarget;

    return {
      current: { stocks: stocksCurrent, fiis: fiisCurrent, td: tdCurrent, total: currentTotal },
      target: { stocks: stocksTarget, fiis: fiisTarget, td: tdTarget, total: targetTotal },
    };
  }, [redistributionResult]);

  const baseCurrentTotals = useMemo(() => {
    if (redistributionTotals) return redistributionTotals.current;
    return { stocks: totals.stocksCurrent, fiis: totals.fiisCurrent, td: totals.tdCurrent, total: totals.total };
  }, [redistributionTotals, totals]);

  const pieCurrent = useMemo(() => ([
    { name: 'Ações', value: baseCurrentTotals.stocks },
    { name: 'FIIs', value: baseCurrentTotals.fiis },
    { name: 'Tesouro', value: baseCurrentTotals.td },
  ]), [baseCurrentTotals]);

  const pieAfter = useMemo(() => {
    if (activeMode === 'allocation' && reallocTotals) {
      return [
        { name: 'Ações', value: reallocTotals.stocks },
        { name: 'FIIs', value: reallocTotals.fiis },
        { name: 'Tesouro', value: reallocTotals.td },
      ];
    }
    if (activeMode === 'redistribution' && redistributionTotals) {
      return [
        { name: 'Ações', value: redistributionTotals.target.stocks },
        { name: 'FIIs', value: redistributionTotals.target.fiis },
        { name: 'Tesouro', value: redistributionTotals.target.td },
      ];
    }
    return [] as { name: string; value: number }[];
  }, [activeMode, reallocTotals, redistributionTotals]);

  const renderOutsidePct = (props: any) => {
    const RAD = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, percent } = props;
    const r = outerRadius + 14;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const text = `${(percent * 100).toFixed(1)}%`;
    return (
      <text x={x} y={y} fill="#EAEAEA" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontWeight: 700 }}>{text}</text>
    );
  };

  const classComparisonData = useMemo(() => {
    if (activeMode === 'allocation' && reallocTotals) {
      return [
        { name: 'Ações', atual: totals.stocksCurrent, apos: reallocTotals.stocks },
        { name: 'FIIs', atual: totals.fiisCurrent, apos: reallocTotals.fiis },
        { name: 'Tesouro', atual: totals.tdCurrent, apos: reallocTotals.td },
      ];
    }
    if (activeMode === 'redistribution' && redistributionTotals) {
      return [
        { name: 'Ações', atual: redistributionTotals.current.stocks, apos: redistributionTotals.target.stocks },
        { name: 'FIIs', atual: redistributionTotals.current.fiis, apos: redistributionTotals.target.fiis },
        { name: 'Tesouro', atual: redistributionTotals.current.td, apos: redistributionTotals.target.td },
      ];
    }
    return null;
  }, [activeMode, reallocTotals, redistributionTotals, totals.fiisCurrent, totals.stocksCurrent, totals.tdCurrent]);

  const redistributionTrades = useMemo(() => {
    if (!redistributionResult) return null;
    const trades = redistributionResult.trades || { stocks: [], fiis: [], tesouro: [] };
    return {
      buy: {
        stocks: trades.stocks.filter((t) => t.action === 'buy'),
        fiis: trades.fiis.filter((t) => t.action === 'buy'),
        td: trades.tesouro.filter((t) => t.action === 'buy'),
      },
      sell: {
        stocks: trades.stocks.filter((t) => t.action === 'sell'),
        fiis: trades.fiis.filter((t) => t.action === 'sell'),
        td: trades.tesouro.filter((t) => t.action === 'sell'),
      },
    };
  }, [redistributionResult]);

  const [allocating, setAllocating] = useState(false);
  const handleAllocation = useCallback(async (data: AllocationFormData) => {
    setAllocating(true);
    try {
      const response = await api.post<Allocation>('wallet/allocation', {
        amount_to_invest: Number((Number(data.amountToInvest || 0) * 100).toFixed(0)),
        v2_engine: true,
      });

      if (response.data.sell.stocks.length > 0) setStocksToSell(response.data.sell.stocks);
      if (response.data.sell.fiis.length > 0) setFiisToSell(response.data.sell.fiis);
      if (response.data.buy.stocks.length > 0) setStocksToBuy(response.data.buy.stocks);
      if (response.data.buy.fiis.length > 0) setFiisToBuy(response.data.buy.fiis);
      if (response.data.buy.tesouro.length > 0) setTDToBuy(response.data.buy.tesouro);

      // compute after totals matching original logic
      const filteredStocks = stocks.filter((s) => !response.data.sell.stocks.includes(s.code));
      const filteredFiis = fiis.filter((f) => !response.data.sell.fiis.includes(f.code));

      const stocksAmountToBuy = response.data.buy.stocks.reduce((sum, s) => sum + s.pending_amount / 100, 0);
      const fiisAmountToBuy = response.data.buy.fiis.reduce((sum, f) => sum + f.pending_amount / 100, 0);
      const tdAmountToBuy = response.data.buy.tesouro.reduce((sum, t) => sum + t.pending_amount / 100, 0);

      const totalReallocStocks = filteredStocks.reduce((sum, s) => sum + (s.unit_price / 100) * s.quantity, 0) + stocksAmountToBuy;
      const totalReallocFiis = filteredFiis.reduce((sum, f) => sum + (f.unit_price / 100) * f.quantity, 0) + fiisAmountToBuy;
      const totalReallocTD = totals.tdCurrent + tdAmountToBuy;
      setReallocTotals({ stocks: totalReallocStocks, fiis: totalReallocFiis, td: totalReallocTD });

      setActiveMode('allocation');
      setRedistributionResult(null);
      setAllocating(false);
    } catch (e) {
      addToast({ type: 'error', title: 'Erro', description: 'Falha ao calcular alocação' });
      setAllocating(false);
    }
  }, [addToast, fiis, stocks, totals.tdCurrent]);

  const handleRedistribution = useCallback(async () => {
    setRedistributing(true);
    try {
      const response = await api.post<RedistributionResponse>('wallet/redistribution');
      setRedistributionResult(response.data);
      setReallocTotals(null);
      setStocksToBuy([]);
      setFiisToBuy([]);
      setTDToBuy([]);
      setStocksToSell([]);
      setFiisToSell([]);
      setActiveMode('redistribution');
    } catch (e) {
      addToast({ type: 'error', title: 'Erro', description: 'Falha ao calcular redistribuição' });
    } finally {
      setRedistributing(false);
    }
  }, [addToast]);

  if (loading) {
    return (
      <Container>
        <Toolbar />
        <Form onSubmit={() => {}}>{/* spacer */}</Form>
        <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingAnimation />
        </div>
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      <Toolbar />
      <Form onSubmit={() => {}}>{/* spacer */}</Form>
      <Content>
        <HeaderRow>
          <Title>Alocação da carteira</Title>
        </HeaderRow>

        <ControlsRow>
          <UnformForm ref={allocateAssetsFormRef} onSubmit={handleAllocation} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input name="amountToInvest" icon={FiDollarSign} placeholder="Quantia disponível para investir" />
            <PrimaryButton variant="primary" type="submit">{allocating ? 'Alocando...' : 'Alocar recursos'}</PrimaryButton>
          </UnformForm>
          <span style={{ color: '#A0A0A0', fontWeight: 700, display: 'inline-flex', alignItems: 'center', height: 56 }}>OU</span>
          <PrimaryButton variant="primary" type="button" onClick={handleRedistribution} disabled={redistributing}>
            {redistributing ? 'Rebalanceando...' : 'Rebalancear carteira'}
          </PrimaryButton>
        </ControlsRow>

        <ChartsGrid>
          <ChartCard>
            <ChartTitle>Distribuição atual de ativos</ChartTitle>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieCurrent} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label={renderOutsidePct} labelLine>
                    {pieCurrent.map((_, i) => (
                      <Cell key={`c-${i}`} fill={palette[i % palette.length]} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={24} />
                  <Tooltip contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }} itemStyle={{ color: '#EAEAEA' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Distribuição após alocação</ChartTitle>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieAfter} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label={renderOutsidePct} labelLine>
                    {pieCurrent.map((_, i) => (
                      <Cell key={`a-${i}`} fill={palette[i % palette.length]} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={24} />
                  <Tooltip contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }} itemStyle={{ color: '#EAEAEA' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </ChartsGrid>

        {/* Optional class comparison bar (current vs after) */}
        {classComparisonData && (
          <ChartCard>
            <ChartTitle>Comparativo de classes (atual vs após)</ChartTitle>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={classComparisonData} margin={{ left: 8, right: 8 }}>
                  <XAxis dataKey="name" stroke="#A0A0A0" />
                  <YAxis stroke="#A0A0A0" />
                  <Tooltip contentStyle={{ background: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)', color: '#EAEAEA' }} itemStyle={{ color: '#EAEAEA' }} />
                  <Legend />
                  <Bar dataKey="atual" name="Atual" fill={palette[0]} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="apos" name="Após" fill={palette[1]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {redistributionTrades && (
          <>
            {redistributionTrades.buy.stocks.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  Ações a comprar (redistribuição) - R$
                  {redistributionTrades.buy.stocks.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.buy.stocks.map((s) => (
                      <tr key={`rbs-${s.code}`}>
                        <td data-label="Código">{s.code}</td>
                        <td data-label="Nome">{s.name}</td>
                        <td data-label="Valor atual">{(s.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(s.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(s.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}

            {redistributionTrades.sell.stocks.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  Ações a vender (redistribuição) - R$
                  {redistributionTrades.sell.stocks.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.sell.stocks.map((s) => (
                      <tr key={`rss-${s.code}`}>
                        <td data-label="Código">{s.code}</td>
                        <td data-label="Nome">{s.name}</td>
                        <td data-label="Valor atual">{(s.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(s.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(s.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}

            {redistributionTrades.buy.fiis.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  FIIs a comprar (redistribuição) - R$
                  {redistributionTrades.buy.fiis.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.buy.fiis.map((f) => (
                      <tr key={`rbf-${f.code}`}>
                        <td data-label="Código">{f.code}</td>
                        <td data-label="Nome">{f.name}</td>
                        <td data-label="Valor atual">{(f.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(f.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(f.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}

            {redistributionTrades.sell.fiis.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  FIIs a vender (redistribuição) - R$
                  {redistributionTrades.sell.fiis.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.sell.fiis.map((f) => (
                      <tr key={`rsf-${f.code}`}>
                        <td data-label="Código">{f.code}</td>
                        <td data-label="Nome">{f.name}</td>
                        <td data-label="Valor atual">{(f.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(f.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(f.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}

            {redistributionTrades.buy.td.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  Tesouro Direto a comprar (redistribuição) - R$
                  {redistributionTrades.buy.td.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.buy.td.map((t) => (
                      <tr key={`rbt-${t.code}`}>
                        <td data-label="Código">{t.code}</td>
                        <td data-label="Nome">{t.name}</td>
                        <td data-label="Valor atual">{(t.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(t.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(t.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}

            {redistributionTrades.sell.td.length > 0 && (
              <ChartCard>
                <ChartTitle>
                  Tesouro Direto a vender (redistribuição) - R$
                  {redistributionTrades.sell.td.reduce((acc, a) => acc + a.amount / 100, 0).toFixed(2)}
                </ChartTitle>
                <Table>
                  <thead><tr><th>Código</th><th>Nome</th><th>Valor atual</th><th>Valor alvo</th><th>Quantia</th></tr></thead>
                  <tbody>
                    {redistributionTrades.sell.td.map((t) => (
                      <tr key={`rst-${t.code}`}>
                        <td data-label="Código">{t.code}</td>
                        <td data-label="Nome">{t.name}</td>
                        <td data-label="Valor atual">{(t.current_value / 100).toFixed(2)}</td>
                        <td data-label="Valor alvo">{(t.target_value / 100).toFixed(2)}</td>
                        <td data-label="Quantia">{(t.amount / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ChartCard>
            )}
          </>
        )}

        {/* Lists */}
        {stocksToBuy.length > 0 && (
          <ChartCard>
            <ChartTitle>
              Ações a comprar - R$
              {stocksToBuy.reduce((acc, a) => acc + a.pending_amount / 100, 0).toFixed(2)}
            </ChartTitle>
            <Table>
              <thead><tr><th>Código</th><th>Nome</th><th>Quantia</th></tr></thead>
              <tbody>
                {stocksToBuy.map((s) => (
                  <tr key={`sb-${s.code}`}><td data-label="Código">{s.code}</td><td data-label="Nome">{s.name}</td><td data-label="Quantia">{(s.pending_amount / 100).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </Table>
          </ChartCard>
        )}

        {stocksToSell.length > 0 && (
          <ChartCard>
            <ChartTitle>Ações a vender</ChartTitle>
            <Table>
              <thead><tr><th>Código</th></tr></thead>
              <tbody>
                {stocksToSell.map((c) => (<tr key={`ss-${c}`}><td data-label="Código">{c}</td></tr>))}
              </tbody>
            </Table>
          </ChartCard>
        )}

        {fiisToBuy.length > 0 && (
          <ChartCard>
            <ChartTitle>
              FIIs a comprar - R$
              {fiisToBuy.reduce((acc, a) => acc + a.pending_amount / 100, 0).toFixed(2)}
            </ChartTitle>
            <Table>
              <thead><tr><th>Código</th><th>Nome</th><th>Quantia</th></tr></thead>
              <tbody>
                {fiisToBuy.map((f) => (
                  <tr key={`fb-${f.code}`}><td data-label="Código">{f.code}</td><td data-label="Nome">{f.name}</td><td data-label="Quantia">{(f.pending_amount / 100).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </Table>
          </ChartCard>
        )}

        {fiisToSell.length > 0 && (
          <ChartCard>
            <ChartTitle>FIIs a vender</ChartTitle>
            <Table>
              <thead><tr><th>Código</th></tr></thead>
              <tbody>
                {fiisToSell.map((c) => (<tr key={`fs-${c}`}><td data-label="Código">{c}</td></tr>))}
              </tbody>
            </Table>
          </ChartCard>
        )}

        {tdToBuy.length > 0 && (
          <ChartCard>
            <ChartTitle>
              Tesouro Direto a comprar - R$
              {tdToBuy.reduce((acc, a) => acc + a.pending_amount / 100, 0).toFixed(2)}
            </ChartTitle>
            <Table>
              <thead><tr><th>Grupo</th><th>Título</th><th>Quantia</th></tr></thead>
              <tbody>
                {tdToBuy.map((t, idx) => (
                  <tr key={`tb-${t.group_id}-${idx}`}><td data-label="Grupo">{t.group_id}</td><td data-label="Título">{t.name.split(',').slice(-1)}</td><td data-label="Quantia">{(t.pending_amount / 100).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </Table>
          </ChartCard>
        )}

      </Content>
    </Container>
  );
};

export default PortfolioAllocationV2;
